import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, Car, ChevronDown, ExternalLink, Gauge, HeartPulse, LayoutDashboard, Link as LinkIcon, Lock, LogOut, Mail, Pencil, Plus, Save, Shield, Sparkles, UserRound, Users2, Utensils, X, Zap } from 'lucide-react';
import logo from '../assets/westcounty-mark.png';
import { API_BASE_URL } from '../config/api.js';
import { socialLinks } from '../config/site.js';

const dashboardText = {
  id: {
    greeting: 'Selamat Datang',
    sidebarBrand: 'GTA:WestCounty',
    dashboard: 'Dashboard',
    characters: 'Characters',
    logout: 'Logout',
    onlineStatus: 'Server Status',
    online: 'Online',
    offline: 'Offline',
    players: 'Players',
    registeredAccounts: 'Registered Account',
    registeredCharacters: 'Registered Characters',
    yourCharacters: 'My Characters',
    selectCharacter: 'Select a character to view full details.',
    noCharacters: 'Belum ada karakter yang terhubung ke akun UCP ini.',
    createCharacter: 'Create Character',
    donation: 'Donasi',
    donationTitle: 'Donasi & Top Up',
    donationDesc: 'Top up VIP, level character, atau gold melalui sistem donasi resmi GTA:WestCounty.',
    donationButton: 'Menuju Donasi',
    viewDetails: 'View Details',
    quickInfo: 'Detail Character',
    cash: 'Uang Tangan',
    bank: 'Uang Bank',
    gold: 'Gold',
    vip: 'Jenis VIP',
    vipTimer: 'Timer VIP',
    vehicles: 'Kendaraan Dimiliki',
    vehicleList: 'Vehicle List',
    health: 'Darah',
    armor: 'Armor',
    hunger: 'Lapar',
    energy: 'Energy',
    level: 'Level',
    skin: 'Skin',
    emptyVehicle: 'Belum memiliki kendaraan.',
    helper: 'Panel UCP untuk melihat status akun, daftar karakter, dan informasi detail karakter GTA:WestCounty.',
    accountSettings: 'Account Settings',
    accountInfo: 'Informasi Akun',
    accountEmail: 'Email',
    accountDiscord: 'Discord',
    connectDiscord: 'Sambungkan ke Discord',
    discordNotLinked: 'Belum terhubung',
    close: 'Tutup',
    oldEmail: 'Email Lama',
    newEmail: 'Email Baru',
    saveSettings: 'Simpan Perubahan',
    cancelEdit: 'Batal',
    editEmail: 'Edit Email',
    verifyWithOldEmail: 'Kirim kode ke email lama',
    verifyWithDiscord: 'Kirim kode ke Discord',
    verificationCode: 'Kode Verifikasi',
    sendCodeFirst: 'Pilih metode pengiriman kode verifikasi.',
    codeSent: 'Kode verifikasi berhasil dikirim.',
    updateSuccess: 'Email berhasil diperbarui.',
    updateFailed: 'Gagal memperbarui account settings.',
    discordHint: 'Hubungkan akun Discord agar profile Discord bisa tampil otomatis.',
    unbindDiscord: 'Lepas Discord',
    sendUnbindCode: 'Kirim kode ke email',
    unbindCodeSent: 'Kode verifikasi pelepasan Discord berhasil dikirim ke email.',
    unbindDiscordSuccess: 'Akun Discord berhasil dilepas.',
    unbindDiscordConfirm: 'Masukkan kode verifikasi untuk melepas akun Discord dari UCP ini.',
    loading: 'Memuat dashboard...',
    failed: 'Gagal memuat dashboard.',
    createNewCharacterTitle: 'Create New Character',
    createNewCharacterDesc: 'Lengkapi data dasar karakter sebelum mulai bermain.',
    characterName: 'Character Name',
    birthDate: 'Tanggal Lahir',
    birthDateHint: 'Format: DD/MM/YYYY',
    origin: 'Origin',
    gender: 'Gender',
    genderMale: 'Male',
    genderFemale: 'Female',
    createCharacterSubmit: 'Create Character',
    createCharacterCancel: 'Cancel',
    createCharacterSuccess: 'Character berhasil dibuat.',
    createCharacterFailed: 'Gagal membuat character.',
    selectOrigin: 'Pilih origin',
    namePlaceholder: 'Contoh: Jouse_Casano',
    birthDatePlaceholder: '11/11/2000',
    noSlotLeft: 'Slot character sudah penuh.',
  },
  en: {
    greeting: 'Welcome Back',
    sidebarBrand: 'GTA:WestCounty',
    dashboard: 'Dashboard',
    characters: 'Characters',
    logout: 'Logout',
    onlineStatus: 'Server Status',
    online: 'Online',
    offline: 'Offline',
    players: 'Players',
    registeredAccounts: 'Registered Account',
    registeredCharacters: 'Registered Characters',
    yourCharacters: 'My Characters',
    selectCharacter: 'Select a character to view full details.',
    noCharacters: 'There are no characters linked to this UCP account yet.',
    createCharacter: 'Create Character',
    donation: 'Donation',
    donationTitle: 'Donation & Top Up',
    donationDesc: 'Top up VIP, character level, or gold through the official GTA:WestCounty donation system.',
    donationButton: 'Go to Donation',
    viewDetails: 'View Details',
    quickInfo: 'Character Detail',
    cash: 'Cash',
    bank: 'Bank Money',
    gold: 'Gold',
    vip: 'VIP Type',
    vipTimer: 'VIP Timer',
    vehicles: 'Owned Vehicles',
    vehicleList: 'Vehicle List',
    health: 'Health',
    armor: 'Armor',
    hunger: 'Hunger',
    energy: 'Energy',
    level: 'Level',
    skin: 'Skin',
    emptyVehicle: 'No owned vehicles yet.',
    helper: 'UCP panel to view account status, character list, and detailed GTA:WestCounty character information.',
    accountSettings: 'Account Settings',
    accountInfo: 'Account Information',
    accountEmail: 'Email',
    accountDiscord: 'Discord',
    connectDiscord: 'Connect Discord',
    discordNotLinked: 'Not linked yet',
    close: 'Close',
    oldEmail: 'Old Email',
    newEmail: 'New Email',
    saveSettings: 'Save Changes',
    cancelEdit: 'Cancel',
    editEmail: 'Edit Email',
    verifyWithOldEmail: 'Send code to old email',
    verifyWithDiscord: 'Send code to Discord',
    verificationCode: 'Verification Code',
    sendCodeFirst: 'Choose where to send the verification code.',
    codeSent: 'Verification code sent successfully.',
    updateSuccess: 'Email updated successfully.',
    updateFailed: 'Failed to update account settings.',
    discordHint: 'Connect your Discord account so the Discord profile can appear automatically.',
    unbindDiscord: 'Unbind Discord',
    sendUnbindCode: 'Send code to email',
    unbindCodeSent: 'Discord unbind verification code sent to email.',
    unbindDiscordSuccess: 'Discord account has been unbound successfully.',
    unbindDiscordConfirm: 'Enter the verification code to unbind this Discord account from your UCP.',
    loading: 'Loading dashboard...',
    failed: 'Failed to load dashboard.',
    createNewCharacterTitle: 'Create New Character',
    createNewCharacterDesc: 'Complete the basic character information before you start playing.',
    characterName: 'Character Name',
    birthDate: 'Birth Date',
    birthDateHint: 'Format: DD/MM/YYYY',
    origin: 'Origin',
    gender: 'Gender',
    genderMale: 'Male',
    genderFemale: 'Female',
    createCharacterSubmit: 'Create Character',
    createCharacterCancel: 'Cancel',
    createCharacterSuccess: 'Character created successfully.',
    createCharacterFailed: 'Failed to create character.',
    selectOrigin: 'Select origin',
    namePlaceholder: 'Example: Jouse_Casano',
    birthDatePlaceholder: '11/11/2000',
    noSlotLeft: 'Character slots are full.',
  },
};

function getStoredAuth() {
  try {
    const raw = localStorage.getItem('westcounty-auth');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function money(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}

const ORIGIN_OPTIONS = [
  'United States Of America',
  'Singapore',
  'Indonesia',
  'Afganistan',
  'Albania',
  'Pakistan',
  'Phillipines',
  'Russian',
  'Qatar',
  'Spanish',
  'Argentina',
  'Arabic',
  'Australia',
  'Bangladesh',
  'Brazil',
  'Bulgaria',
  'Canada',
  'China',
  'Colombia',
  'Congo',
  'Denmark',
  'Italian',
  'German',
  'HongKong',
  'India',
  'Iran',
  'Iraq',
  'Mexico',
  'Malaysia',
  'Thailand',
  'Japan'
];

function formatBirthInput(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function clampStat(value) {
  const number = Number(value || 0);
  return Math.max(0, Math.min(100, number));
}

function vehicleName(model) {
  const names = {
    400: 'Landstalker', 401: 'Bravura', 402: 'Buffalo', 405: 'Sentinel', 411: 'Infernus', 415: 'Cheetah', 421: 'Washington',
    426: 'Premier', 429: 'Banshee', 439: 'Stallion', 445: 'Admiral', 451: 'Turismo', 461: 'PCJ-600', 462: 'Faggio',
    468: 'Sanchez', 475: 'Sabre', 477: 'ZR-350', 480: 'Comet', 492: 'Greenwood', 506: 'Super GT', 507: 'Elegant',
    516: 'Nebula', 517: 'Majestic', 526: 'Fortune', 527: 'Cadrona', 529: 'Willard', 533: 'Feltzer', 541: 'Bullet',
    547: 'Primo', 550: 'Sunrise', 551: 'Merit', 560: 'Sultan', 562: 'Elegy', 565: 'Flash', 566: 'Tahoma',
    567: 'Savanna', 575: 'Broadway', 579: 'Huntley', 580: 'Stafford', 585: 'Emperor', 587: 'Euros', 589: 'Club', 602: 'Alpha', 603: 'Phoenix'
  };

  return names[Number(model)] || `Model ${model}`;
}

function skinImage(skin) {
  const id = Number(skin || 0);
  return `https://assets.open.mp/assets/images/skins/${id}.png`;
}

function vehicleImage(model) {
  const id = Number(model || 400);
  return `https://assets.open.mp/assets/images/vehiclePictures/Vehicle_${id}.jpg`;
}

function vehicleFallbackImage(model) {
  const id = Number(model || 400);
  return `https://assets.open.mp/assets/images/vehicles/${id}.jpg`;
}

function StatBar({ icon, label, value, tone = 'health' }) {
  const percent = clampStat(value);
  const toneClass = tone === 'health'
    ? 'is-health'
    : tone === 'armor'
      ? 'is-armor'
      : percent <= 30
        ? 'is-low'
        : percent <= 65
          ? 'is-medium'
          : 'is-good';

  return (
    <div className="character-statbar">
      <div className="statbar-label">
        <span>{icon} {label}</span>
        <b>{percent}%</b>
      </div>
      <div className={`statbar-track ${toneClass}`} style={{ '--fill': `${percent}%` }}>
        <i />
      </div>
    </div>
  );
}

function GtaSkin({ skin, name, size = 'card' }) {
  return (
    <div className={`gta-skin gta-skin-${size}`}>
      <img
        src={skinImage(skin)}
        alt={name}
        loading="lazy"
        onError={(event) => {
          event.currentTarget.style.display = 'none';
          event.currentTarget.parentElement?.classList.add('is-missing');
        }}
      />
    </div>
  );
}


function AccountSettingsModal({ account, t, onClose, onUpdated }) {
  const [newEmail, setNewEmail] = useState(account?.email || '');
  const [editingEmail, setEditingEmail] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingMethod, setSendingMethod] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [originOpen, setOriginOpen] = useState(false);
  const [genderOpen, setGenderOpen] = useState(false);
  const [editingDiscord, setEditingDiscord] = useState(false);
  const [discordVerificationSent, setDiscordVerificationSent] = useState(false);
  const [discordVerificationCode, setDiscordVerificationCode] = useState('');
  const [discordSaving, setDiscordSaving] = useState(false);
  const [discordStatus, setDiscordStatus] = useState(null);

  if (!account) return null;

  const discordId = String(account.discord_id || '').trim();
  const hasDiscord = discordId !== '' && discordId !== '0';
  const discordAvatar = account.discord_avatar || '';
  const discordConnectUrl = `${API_BASE_URL}/discord-start.php?username=${encodeURIComponent(account.username)}`;
  const emailChanged = newEmail.trim().toLowerCase() !== String(account.email || '').trim().toLowerCase();

  function resetEdit() {
    setNewEmail(account?.email || '');
    setEditingEmail(false);
    setVerificationSent(false);
    setVerificationCode('');
    setSendingMethod('');
    setStatus(null);
  }

  function resetDiscordEdit() {
    setEditingDiscord(false);
    setDiscordVerificationSent(false);
    setDiscordVerificationCode('');
    setDiscordStatus(null);
  }

  async function requestEmailCode(method) {
    setStatus(null);

    if (!emailChanged) {
      setStatus({ type: 'error', text: 'Email baru belum berubah.' });
      return;
    }

    setSaving(true);
    setSendingMethod(method);

    try {
      const response = await fetch(`${API_BASE_URL}/request-email-change.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: account.username,
          new_email: newEmail,
          method,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.message || t.updateFailed);
      }

      setVerificationSent(true);
      setStatus({ type: 'success', text: data.message || t.codeSent });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || t.updateFailed });
      setSendingMethod('');
    } finally {
      setSaving(false);
    }
  }

  async function verifyEmailCode(event) {
    event.preventDefault();
    setStatus(null);

    if (!verificationCode.trim()) {
      setStatus({ type: 'error', text: t.verificationCode + ' wajib diisi.' });
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE_URL}/verify-email-change.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: account.username,
          new_email: newEmail,
          code: verificationCode,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.message || t.updateFailed);
      }

      setStatus({ type: 'success', text: data.message || t.updateSuccess });
      setEditingEmail(false);
      setVerificationSent(false);
      setVerificationCode('');
      setSendingMethod('');
      if (data.account) {
        onUpdated?.(data.account);
        setNewEmail(data.account.email || newEmail);
      }
    } catch (error) {
      setStatus({ type: 'error', text: error.message || t.updateFailed });
    } finally {
      setSaving(false);
    }
  }

  async function requestDiscordUnbindCode() {
    setDiscordStatus(null);
    setDiscordSaving(true);

    try {
      const response = await fetch(`${API_BASE_URL}/request-discord-unbind.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: account.username }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.message || t.updateFailed);
      }

      setDiscordVerificationSent(true);
      setDiscordStatus({ type: 'success', text: data.message || t.unbindCodeSent });
    } catch (error) {
      setDiscordStatus({ type: 'error', text: error.message || t.updateFailed });
    } finally {
      setDiscordSaving(false);
    }
  }

  async function verifyDiscordUnbind(event) {
    event.preventDefault();
    setDiscordStatus(null);

    if (!discordVerificationCode.trim()) {
      setDiscordStatus({ type: 'error', text: t.verificationCode + ' wajib diisi.' });
      return;
    }

    setDiscordSaving(true);

    try {
      const response = await fetch(`${API_BASE_URL}/verify-discord-unbind.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: account.username, code: discordVerificationCode }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.message || t.updateFailed);
      }

      setDiscordStatus({ type: 'success', text: data.message || t.unbindDiscordSuccess });
      const updatedAccount = data.account || {
        ...account,
        discord_id: '',
        discord_username: '',
        discord_avatar: '',
      };
      onUpdated?.(updatedAccount);
      resetDiscordEdit();
    } catch (error) {
      setDiscordStatus({ type: 'error', text: error.message || t.updateFailed });
    } finally {
      setDiscordSaving(false);
    }
  }


  return (
    <div className="character-modal-backdrop account-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <article className="character-modal account-settings-modal" role="dialog" aria-modal="true" aria-label={t.accountSettings} onMouseDown={(event) => event.stopPropagation()}>
        <button className="character-modal-close" type="button" onClick={onClose} aria-label={t.close}>
          <X size={18} />
        </button>

        <div className="account-settings-head">
          <div className="account-settings-avatar">
            {hasDiscord ? (
              <img src={discordAvatar} alt={account.username} onError={(event) => { event.currentTarget.style.display = 'none'; event.currentTarget.parentElement?.classList.add('is-fallback'); }} />
            ) : null}
            <span><UserRound size={22} /></span>
          </div>

          <div>
            <span className="modal-label">{t.accountInfo}</span>
            <h2>{account.username}</h2>
            <p>UCP ID #{account.reg_id}</p>
          </div>
        </div>

        <div className="account-settings-grid">
          <form className="account-settings-form" onSubmit={verifyEmailCode}>
            <div className="settings-read-row">
              <span>{t.accountEmail}</span>
              <div className="settings-value-box">
                <Mail size={15} />
                <strong>{account.email || '-'}</strong>
                <button className="settings-pencil" type="button" onClick={() => { setEditingEmail((value) => !value); setStatus(null); setVerificationSent(false); setVerificationCode(''); }} aria-label={t.editEmail}>
                  <Pencil size={14} />
                </button>
              </div>
            </div>

            {editingEmail ? (
              <div className="settings-edit-area email-only-edit">
                <label>
                  <span>{t.oldEmail}</span>
                  <div className="settings-input is-readonly"><Mail size={15} /><input type="email" value={account.email || ''} readOnly /></div>
                </label>

                <label>
                  <span>{t.newEmail}</span>
                  <div className="settings-input"><Mail size={15} /><input type="email" value={newEmail} onChange={(event) => { setNewEmail(event.target.value); setVerificationSent(false); setVerificationCode(''); }} placeholder="emailbaru@gmail.com" autoFocus /></div>
                </label>

                {!verificationSent ? (
                  <div className="settings-method-row">
                    <p>{t.sendCodeFirst}</p>
                    <div>
                      <button className="settings-save-btn" type="button" disabled={saving || !emailChanged} onClick={() => requestEmailCode('email')}>
                        <Mail size={16} /> {saving && sendingMethod === 'email' ? 'Sending...' : t.verifyWithOldEmail}
                      </button>
                      <button className="settings-save-btn secondary" type="button" disabled={saving || !emailChanged || !hasDiscord} onClick={() => requestEmailCode('discord')}>
                        <LinkIcon size={16} /> {saving && sendingMethod === 'discord' ? 'Sending...' : t.verifyWithDiscord}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="settings-code-row">
                    <label>
                      <span>{t.verificationCode}</span>
                      <div className="settings-input"><AlertCircle size={15} /><input value={verificationCode} onChange={(event) => setVerificationCode(event.target.value)} placeholder="Masukkan kode verifikasi" /></div>
                    </label>
                    <div className="settings-action-row">
                      <button className="settings-save-btn" type="submit" disabled={saving}>
                        <Save size={16} /> {saving ? 'Saving...' : t.saveSettings}
                      </button>
                      <button className="settings-cancel-btn" type="button" onClick={resetEdit}>{t.cancelEdit}</button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {status ? <div className={`settings-status ${status.type}`}><AlertCircle size={15} /> {status.text}</div> : null}
          </form>

          <div className="account-info-card discord-card">
            <div className="settings-inline-head">
              <span>{t.accountDiscord}</span>
              {hasDiscord ? (
                <button className="settings-edit-icon" type="button" onClick={() => { setEditingDiscord(true); setDiscordStatus(null); }}>
                  <Pencil size={14} />
                </button>
              ) : null}
            </div>

            {hasDiscord ? (
              <>
                <b><LinkIcon size={15} /> {account.discord_username ? `${account.discord_username} (${discordId})` : discordId}</b>
                {editingDiscord ? (
                  <form className="discord-unbind-panel" onSubmit={verifyDiscordUnbind}>
                    <p>{t.unbindDiscordConfirm}</p>
                    {!discordVerificationSent ? (
                      <div className="settings-action-row">
                        <button className="settings-save-btn" type="button" onClick={requestDiscordUnbindCode} disabled={discordSaving}>
                          <Mail size={16} /> {discordSaving ? 'Sending...' : t.sendUnbindCode}
                        </button>
                        <button className="settings-cancel-btn" type="button" onClick={resetDiscordEdit}>{t.cancelEdit}</button>
                      </div>
                    ) : (
                      <>
                        <label>
                          <span>{t.verificationCode}</span>
                          <div className="settings-input"><AlertCircle size={15} /><input value={discordVerificationCode} onChange={(event) => setDiscordVerificationCode(event.target.value)} placeholder="Masukkan kode verifikasi" /></div>
                        </label>
                        <div className="settings-action-row">
                          <button className="settings-save-btn" type="submit" disabled={discordSaving}>
                            <Save size={16} /> {discordSaving ? 'Saving...' : t.unbindDiscord}
                          </button>
                          <button className="settings-cancel-btn" type="button" onClick={resetDiscordEdit}>{t.cancelEdit}</button>
                        </div>
                      </>
                    )}

                    {discordStatus ? <div className={`settings-status ${discordStatus.type}`}><AlertCircle size={15} /> {discordStatus.text}</div> : null}
                  </form>
                ) : null}
              </>
            ) : (
              <div className="discord-connect-row">
                <div>
                  <b>{t.discordNotLinked}</b>
                  <small>{t.discordHint}</small>
                </div>
                <a className="connect-discord-btn" href={discordConnectUrl}>
                  {t.connectDiscord} <ExternalLink size={14} />
                </a>
              </div>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}

function CharacterDetailModal({ character, t, onClose }) {
  if (!character) return null;

  return (
    <div className="character-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <article className="character-modal" role="dialog" aria-modal="true" aria-label={character.name} onMouseDown={(event) => event.stopPropagation()}>
        <button className="character-modal-close" type="button" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        <div className="character-modal-hero">
          <GtaSkin skin={character.skin} name={character.name} size="detail" />
          <div>
            <span className="modal-label">{t.quickInfo}</span>
            <h2>{character.name}</h2>
            <p>{t.level}: {character.level} · {t.skin} #{character.skin}</p>
          </div>
        </div>

        <div className="character-detail-grid modal-grid">
          <div><span>{t.cash}</span><b>${money(character.money)}</b></div>
          <div><span>{t.bank}</span><b>${money(character.bank)}</b></div>
          <div><span>{t.gold}</span><b>{character.gold}</b></div>
          <div><span>{t.vip}</span><b>{character.vip_label}</b></div>
          <div><span>{t.vipTimer}</span><b>{character.vip_timer}</b></div>
          <div><span>{t.vehicles}</span><b>{character.vehicles_count}</b></div>
        </div>

        <div className="character-bars">
          <StatBar icon={<HeartPulse size={15} />} label={t.health} value={character.health} tone="health" />
          <StatBar icon={<Shield size={15} />} label={t.armor} value={character.armor} tone="armor" />
          <StatBar icon={<Utensils size={15} />} label={t.hunger} value={character.hunger} tone="survival" />
          <StatBar icon={<Zap size={15} />} label={t.energy} value={character.energy} tone="survival" />
        </div>

        <div className="vehicle-list-block">
          <h3><Car size={16} /> {t.vehicleList}</h3>
          {character.vehicles_loading ? (
            <p className="empty-vehicle">Memuat kendaraan...</p>
          ) : character.vehicles?.length ? (
            <div className="vehicle-card-grid">
              {character.vehicles.map((vehicle) => (
                <div className="vehicle-gta-card" key={vehicle.id}>
                  <div className="vehicle-gta-image">
                    <img
                      src={vehicleImage(vehicle.model)}
                      alt={vehicleName(vehicle.model)}
                      loading="lazy"
                      onError={(event) => {
                        if (!event.currentTarget.dataset.fallback) {
                          event.currentTarget.dataset.fallback = '1';
                          event.currentTarget.src = vehicleFallbackImage(vehicle.model);
                          return;
                        }

                        event.currentTarget.style.display = 'none';
                        event.currentTarget.parentElement?.classList.add('is-missing');
                      }}
                    />
                  </div>
                  <div>
                    <strong>{vehicleName(vehicle.model)}</strong>
                    <span>Plate: {vehicle.plate || 'None'} · ID #{vehicle.id}</span>
                    <small><Gauge size={13} /> Fuel {vehicle.fuel} · HP {vehicle.health}</small>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-vehicle">{t.emptyVehicle}</p>
          )}
        </div>
      </article>
    </div>
  );
}


function CreateCharacterModal({ open, onClose, onCreated, authUsername, t }) {
  const [form, setForm] = useState({
    character_name: '',
    birth_date: '',
    origin: ORIGIN_OPTIONS[0],
    gender: 'Male',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [originOpen, setOriginOpen] = useState(false);
  const [genderOpen, setGenderOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ character_name: '', birth_date: '', origin: ORIGIN_OPTIONS[0], gender: 'Male' });
      setStatus(null);
      setSubmitting(false);
      setOriginOpen(false);
      setGenderOpen(false);
    }
  }, [open]);

  if (!open) return null;

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/create-character.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: authUsername,
          character_name: form.character_name.trim(),
          birth_date: form.birth_date.trim(),
          origin: form.origin,
          gender: form.gender,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || t.createCharacterFailed);
      }

      setStatus({ type: 'success', text: data.message || t.createCharacterSuccess });
      onCreated?.();
      setTimeout(() => onClose?.(), 500);
    } catch (error) {
      setStatus({ type: 'error', text: error.message || t.createCharacterFailed });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="character-modal-backdrop create-character-backdrop" onClick={onClose}>
      <div className="character-modal create-character-modal" onClick={(event) => event.stopPropagation()}>
        <button className="character-modal-close" type="button" onClick={onClose}><X size={20} /></button>

        <div className="create-character-head">
          <h2>{t.createNewCharacterTitle}</h2>
          <p>{t.createNewCharacterDesc}</p>
        </div>

        <form className="create-character-form" onSubmit={handleSubmit}>
          <div className="create-character-grid">
            <label>
              <span>{t.characterName}</span>
              <input
                type="text"
                value={form.character_name}
                onChange={(event) => updateField('character_name', event.target.value)}
                placeholder={t.namePlaceholder}
                autoComplete="off"
                required
              />
            </label>

            <label>
              <span>{t.birthDate}</span>
              <div className="create-character-input has-icon">
                <CalendarDays size={16} />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.birth_date}
                  onChange={(event) => updateField('birth_date', formatBirthInput(event.target.value))}
                  placeholder={t.birthDatePlaceholder}
                  required
                />
              </div>
              <small>{t.birthDateHint}</small>
            </label>

            <label className="origin-field">
              <span>{t.origin}</span>
              <div className={originOpen ? 'custom-origin-select is-open' : 'custom-origin-select'}>
                <button
                  type="button"
                  className="custom-origin-trigger"
                  onClick={() => setOriginOpen((value) => !value)}
                  aria-expanded={originOpen}
                >
                  <span>{form.origin || t.selectOrigin}</span>
                  <ChevronDown size={16} />
                </button>

                {originOpen ? (
                  <div className="custom-origin-menu">
                    {ORIGIN_OPTIONS.map((origin) => (
                      <button
                        key={origin}
                        type="button"
                        className={form.origin === origin ? 'is-selected' : ''}
                        onClick={() => {
                          updateField('origin', origin);
                          setOriginOpen(false);
                        }}
                      >
                        {origin}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </label>

            <label className="origin-field">
              <span>{t.gender}</span>
              <div className={genderOpen ? 'custom-origin-select is-open' : 'custom-origin-select'}>
                <button
                  type="button"
                  className="custom-origin-trigger"
                  onClick={() => setGenderOpen((value) => !value)}
                  aria-expanded={genderOpen}
                >
                  <span>{form.gender}</span>
                  <ChevronDown size={16} />
                </button>

                {genderOpen ? (
                  <div className="custom-origin-menu">
                    {[
                      { value: 'Male', label: t.genderMale },
                      { value: 'Female', label: t.genderFemale },
                    ].map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        className={form.gender === item.value ? 'is-selected' : ''}
                        onClick={() => {
                          updateField('gender', item.value);
                          setGenderOpen(false);
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </label>
          </div>

          {status ? <div className={`create-character-status ${status.type}`}><AlertCircle size={15} /> {status.text}</div> : null}

          <div className="create-character-actions">
            <button className="create-character-cancel" type="button" onClick={onClose}>{t.createCharacterCancel}</button>
            <button className="create-character-submit" type="submit" disabled={submitting}>
              {submitting ? 'Processing...' : t.createCharacterSubmit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DashboardPage({ lang = 'id' }) {
  const t = dashboardText[lang] || dashboardText.id;
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accountOverride, setAccountOverride] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [vehicleCache, setVehicleCache] = useState({});
  const auth = useMemo(() => getStoredAuth(), []);

  const account = accountOverride || dashboard?.account || {};
  const accountDiscordId = String(account.discord_id || '').trim();
  const accountAvatar = account.discord_avatar || '';
  const summary = dashboard?.summary || {};
  const serverOnline = summary.serverOnline !== false;
  const serverAddress = summary.serverAddress || '15.235.133.208:7007';
  const serverSourceText = summary.serverSource === 'sftp_json'
    ? 'Data dari discord_status.json'
    : serverAddress;
  const characters = useMemo(() => dashboard?.characters || [], [dashboard]);
  const detailBase = characters.find((character) => character.id === detailId) || null;
  const detailVehicleCache = detailId ? vehicleCache[detailId] : null;
  const detailCharacter = detailBase
    ? {
        ...detailBase,
        vehicles: detailVehicleCache?.vehicles || detailBase.vehicles || [],
        vehicles_count: detailVehicleCache?.vehicles_count ?? detailBase.vehicles_count,
        vehicles_loading: detailVehicleCache?.loading || false,
      }
    : null;

  useEffect(() => {
    function closeMenus(event) {
      if (!event.target.closest('.dashboard-account-menu')) {
        setAccountOpen(false);
      }
    }

    document.addEventListener('click', closeMenus);
    return () => document.removeEventListener('click', closeMenus);
  }, []);

  useEffect(() => {
    if (!auth?.username) {
      window.location.href = '/account/login';
      return;
    }

    const controller = new AbortController();

    async function loadDashboard() {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_BASE_URL}/dashboard.php?username=${encodeURIComponent(auth.username)}`, {
          signal: controller.signal,
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data.success) {
          throw new Error(data.message || t.failed);
        }

        setDashboard(data);
        setVehicleCache({});
        setAccountOverride(null);
      } catch (fetchError) {
        if (fetchError.name !== 'AbortError') {
          setError(fetchError.message || t.failed);
        }
      } finally {
        setLoading(false);
      }
    }

    loadDashboard();

    return () => controller.abort();
  }, [auth?.username, t.failed, refreshKey]);

  useEffect(() => {
    if (!detailId || !auth?.username) return;

    const baseCharacter = characters.find((character) => character.id === detailId);
    if (!baseCharacter || Number(baseCharacter.vehicles_count || 0) <= 0) return;

    const cached = vehicleCache[detailId];
    if (cached?.loaded || cached?.loading) return;

    const controller = new AbortController();
    setVehicleCache((current) => ({
      ...current,
      [detailId]: {
        ...(current[detailId] || {}),
        loading: true,
        loaded: false,
        vehicles: current[detailId]?.vehicles || [],
        vehicles_count: current[detailId]?.vehicles_count ?? baseCharacter.vehicles_count,
      },
    }));

    fetch(`${API_BASE_URL}/vehicles.php?username=${encodeURIComponent(auth.username)}&character_id=${encodeURIComponent(detailId)}`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Gagal memuat kendaraan.');
        }
        return data;
      })
      .then((data) => {
        setVehicleCache((current) => ({
          ...current,
          [detailId]: {
            loading: false,
            loaded: true,
            vehicles_count: Number(data.vehicles_count || 0),
            vehicles: data.vehicles || [],
          },
        }));
      })
      .catch((fetchError) => {
        if (fetchError.name === 'AbortError') return;
        setVehicleCache((current) => ({
          ...current,
          [detailId]: {
            ...(current[detailId] || {}),
            loading: false,
            loaded: true,
            vehicles: current[detailId]?.vehicles || [],
            vehicles_count: current[detailId]?.vehicles_count ?? baseCharacter.vehicles_count,
          },
        }));
      });

    return () => controller.abort();
  }, [detailId, auth?.username, characters, vehicleCache]);

  function handleLogout() {
    localStorage.removeItem('westcounty-auth');
    window.location.href = '/account/login';
  }

  function openCharacters() {
    setActiveMenu('characters');
  }

  function handleAccountUpdated(updatedAccount) {
    const merged = { ...account, ...updatedAccount };
    setAccountOverride(merged);
    localStorage.setItem('westcounty-auth', JSON.stringify({
      reg_id: merged.reg_id,
      username: merged.username,
      email: merged.email,
      discord_id: merged.discord_id || '',
      discord_username: merged.discord_username || '',
      discord_avatar: merged.discord_avatar || '',
      verified: merged.verified ?? true,
      logged_in_at: Date.now(),
    }));
  }

  return (
    <main className="dashboard-page">
      <div className="dashboard-noise" />
      <aside className="dashboard-sidebar reveal is-visible">
        <a className="dashboard-brand" href="/" aria-label="GTA:WestCounty Home">
          <img src={logo} alt="GTA:WestCounty" />
          <span>{t.sidebarBrand}</span>
        </a>

        <nav className="dashboard-nav">
          <button className={activeMenu === 'dashboard' ? 'dash-nav-btn is-active' : 'dash-nav-btn'} onClick={() => setActiveMenu('dashboard')}>
            <LayoutDashboard size={17} /> {t.dashboard}
          </button>
          <button className={activeMenu === 'characters' ? 'dash-nav-btn is-active' : 'dash-nav-btn'} onClick={openCharacters}>
            <Users2 size={17} /> {t.characters}
          </button>
          <a className="dash-nav-btn dashboard-donation-link" href="/topup?from=dashboard">
            <Sparkles size={17} /> {t.donation}
          </a>
        </nav>

      </aside>

      <section className="dashboard-main">
        <header className="dashboard-top reveal is-visible">
          <div>
            <h1>{activeMenu === 'characters' ? t.yourCharacters : `${t.greeting}, ${account.username || auth?.username || 'Player'}`}</h1>
            <p>{activeMenu === 'characters' ? t.selectCharacter : t.helper}</p>
          </div>

          {activeMenu === 'characters' ? (
            <button className="create-character-btn" type="button" onClick={() => setCreateOpen(true)}><Plus size={17} /> {t.createCharacter}</button>
          ) : (
            <div className="dashboard-account-menu">
              <button className="dashboard-user-chip" type="button" onClick={(event) => { event.stopPropagation(); setAccountOpen((value) => !value); }}>
                <span className="dashboard-user-avatar">
                  {accountAvatar ? <img src={accountAvatar} alt={account.username || auth?.username || 'Player'} onError={(event) => { event.currentTarget.style.display = 'none'; event.currentTarget.parentElement?.classList.add('is-fallback'); }} /> : null}
                  <i><UserRound size={18} /></i>
                </span>
                <strong>{account.username || auth?.username || 'Player'}</strong>
              </button>

              {accountOpen ? (
                <div className="account-dropdown" onClick={(event) => event.stopPropagation()}>
                  <button className="account-menu-item" type="button" onClick={() => { setSettingsOpen(true); setAccountOpen(false); }}>
                    <UserRound size={16} /> {t.accountSettings}
                  </button>
                  <button className="account-menu-item is-danger" type="button" onClick={handleLogout}>
                    <LogOut size={16} /> {t.logout}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </header>

        {loading ? (
          <div className="dashboard-state reveal is-visible">{t.loading}</div>
        ) : error ? (
          <div className="dashboard-state error reveal is-visible">{error}</div>
        ) : activeMenu === 'characters' ? (
          <section className="characters-page characters-page-clean reveal is-visible">
            {characters.length > 0 ? (
              <div className="character-cards-area character-cards-only">
                {characters.map((character) => (
                  <article className="character-profile-card gta-character-card" key={character.id}>
                    <GtaSkin skin={character.skin} name={character.name} />

                    <div className="character-card-panel">
                      <div>
                        <strong>{character.name}</strong>
                        <span>{t.level}: {character.level}</span>
                      </div>

                      <div className="character-money-row">
                        <b>${money(character.money)}</b>
                        <span>{t.bank} ${money(character.bank)}</span>
                      </div>

                      <div className="character-card-meta">
                        <span><Sparkles size={13} /> {character.gold}</span>
                        <span>{character.vip_label}</span>
                        <button type="button" onClick={() => setDetailId(character.id)}>{t.viewDetails} →</button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="dashboard-empty">{t.noCharacters}</div>
            )}
          </section>
        ) : (
          <>
            <section className="dashboard-stats reveal is-visible">
              <article className={serverOnline ? 'dash-stat-card' : 'dash-stat-card is-offline'}>
                <span>{t.onlineStatus}</span>
                <strong className={serverOnline ? 'is-online' : 'is-offline'}>{serverOnline ? t.online : t.offline}</strong>
                <p>{t.players} {summary.onlinePlayers ?? 0} / {summary.maxPlayers ?? 200}</p>
                <small>{serverSourceText}</small>
              </article>

              <article className="dash-stat-card">
                <span>{t.registeredAccounts}</span>
                <strong>{summary.registeredAccounts ?? 0}</strong>
                <p>UCP</p>
              </article>

              <article className="dash-stat-card">
                <span>{t.registeredCharacters}</span>
                <strong>{summary.registeredCharacters ?? 0}</strong>
                <p>Character</p>
              </article>
            </section>

            <section className="dashboard-bottom-grid reveal is-visible">
              <article className="dashboard-block dashboard-info-card">
                <div className="dashboard-block-head">
                  <h2>{t.yourCharacters}</h2>
                </div>
                <div className="dashboard-character-grid compact">
                  {characters.length ? characters.map((character) => (
                    <button className="dashboard-character-card" key={character.id} onClick={() => { setActiveMenu('characters'); }}>
                      <GtaSkin skin={character.skin} name={character.name} size="mini" />
                      <div className="character-info">
                        <strong>{character.name}</strong>
                        <span>{t.level}: {character.level} · ${money(character.money)}</span>
                      </div>
                    </button>
                  )) : <div className="dashboard-empty">{t.noCharacters}</div>}
                </div>

                <div className="dashboard-donation-card">
                  <div>
                    <span><Sparkles size={15} /> {t.donationTitle}</span>
                    <p>{t.donationDesc}</p>
                  </div>
                  <a href="/topup?from=dashboard">{t.donationButton} <ExternalLink size={15} /></a>
                </div>
              </article>
            </section>
          </>
        )}
      </section>

      <CreateCharacterModal open={createOpen} onClose={() => setCreateOpen(false)} authUsername={auth?.username || ''} t={t} onCreated={() => { setRefreshKey((value) => value + 1); setActiveMenu('characters'); }} />
      {settingsOpen ? <AccountSettingsModal account={account} t={t} onClose={() => setSettingsOpen(false)} onUpdated={handleAccountUpdated} /> : null}
      <CharacterDetailModal character={detailCharacter} t={t} onClose={() => setDetailId(null)} />
    </main>
  );
}
